import React from 'react';
import { X } from 'lucide-react';
import type { SubLeaseFilter } from '../../types/lease';

interface SubLeaseFilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  filters: SubLeaseFilter;
  onFilterChange: (filters: Partial<SubLeaseFilter>) => void;
  holders: string[];
}

export default function SubLeaseFilterPanel({
  isOpen,
  onClose,
  filters,
  onFilterChange,
  holders
}: SubLeaseFilterPanelProps) {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50"
        onClick={onClose}
      />
      {/* Panel */}
      <div className="fixed inset-x-0 bottom-0 sm:inset-auto sm:absolute sm:right-0 sm:top-full sm:mt-2 z-50">
        <div className="w-full sm:w-96 bg-white rounded-t-lg sm:rounded-lg shadow-lg border border-gray-200">
          <div className="p-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Filters</h3>
              <button 
                onClick={onClose} 
                className="p-2 -mr-2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Leaseholder
                </label>
                <select
                  value={filters.holder || ''}
                  onChange={(e) => onFilterChange({ holder: e.target.value || null })}
                  className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">All Leaseholders</option>
                  {holders.map((holder) => (
                    <option key={holder} value={holder}>
                      {holder}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lease End Date Range
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="date"
                    value={filters.endDateFrom || ''}
                    onChange={(e) => onFilterChange({ endDateFrom: e.target.value || null })}
                    className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="From"
                  />
                  <input
                    type="date"
                    value={filters.endDateTo || ''}
                    onChange={(e) => onFilterChange({ endDateTo: e.target.value || null })}
                    className="w-full h-12 sm:h-auto rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="To"
                  />
                </div>
              </div>

              <div className="pt-4 border-t">
                <button
                  onClick={() => onFilterChange({
                    holder: null,
                    status: null,
                    endDateFrom: null,
                    endDateTo: null,
                    leaseDealingNo: null
                  })}
                  className="w-full h-12 sm:h-auto px-4 py-2 text-sm text-primary hover:bg-primary/5 rounded-md"
                >
                  Clear All Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
